import { useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RetailerLookupButton from '../retailerLookupButton.js';
import {
  jidsNRCForms_NRCDowntime,
  dispatchStatusOptions,
  dispositionOptions,
} from '../../../../../data/forms/nrc/nrcDowntimeDropdownOptions.js';
import axios from 'axios';

const NRCDowntimeForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    dispatchStatus: '',
    disposition: '',
    reason: '',
    issue: '',
    caseNotes: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'disposition' && value !== 'Dispatch') {
      setForm(prevForm => ({
        ...prevForm,
        dispatchStatus: '',
      }));
    }
  };

  const handleReset = () => {
    setForm({
      ...form,
      jurisdiction: '',
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      dispatchStatus: '',
      disposition: '',
      reason: '',
      issue: '',
      caseNotes: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/nrcDowntime/submit`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleReset();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const { jurisdiction, retailerId, retailerName, address, phoneNumber, disposition } = form;

    if (!jurisdiction || !retailerId || !retailerName || !address || !phoneNumber || !disposition) {
      return false;
    }

    if (disposition === 'Dispatch' && !form.dispatchStatus) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{translations.jurisdiction || 'Jurisdiction'}</InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jidsNRCForms_NRCDowntime.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Retailer Name'
            name='retailerName'
            value={form.retailerName}
            onChange={handleChange}
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            required
            value={form.phoneNumber}
            onChange={handleChange}
            label='Phone Number'
            name='phoneNumber'
          />
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            required
          >
            <InputLabel>Disposition</InputLabel>
            <Select
              value={form.disposition}
              label={'Disposition'}
              onChange={handleChange}
              name='disposition'
            >
              {dispositionOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Issue'
            name='issue'
            value={form.issue}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip title={'Enter a Retailer ID (numeric values only) to lookup'} arrow>
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <Tooltip title={form.disposition !== 'Dispatch' ? 'Disposition must be Dispatch to enable this field' : ''} arrow>
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.disposition === 'Dispatch'}
              disabled={form.disposition !== 'Dispatch'}
            >
              <InputLabel>Dispatch Status</InputLabel>
              <Select
                value={form.dispatchStatus}
                label={'Dispatch Status'}
                onChange={handleChange}
                name='dispatchStatus'
              >
                {dispatchStatusOptions.map(jid => (
                  <MenuItem value={jid}>{jid}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <TextField
            label='Reason'
            name='reason'
            value={form.reason}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Case Notes'
            name='caseNotes'
            value={form.caseNotes}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Tooltip
            title={
              !isFormValid() ? 'Please fill in all required fields to enable the Submit button' : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default NRCDowntimeForm;
